import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { authAPI } from '../api';
import { useAuth } from '../contexts/AuthContext';

// Landing page for the link in the confirmation email. Consumes the token,
// signs the user in, and drops them straight into chat — they came here to
// start using the product, not to read a success page.

const VerifyEmail = () => {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const { verifyEmail } = useAuth();

  const [state, setState] = useState(token ? 'checking' : 'missing');
  const [message, setMessage] = useState('');
  const [expired, setExpired] = useState(false);

  const [email, setEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendNote, setResendNote] = useState('');

  // React 18 StrictMode mounts effects twice in development. The token is
  // single-use, so a second call would report "already used" and overwrite a
  // success that already happened.
  const ranRef = useRef(false);
  const redirectRef = useRef(null);

  useEffect(() => {
    if (!token || ranRef.current) return;
    ranRef.current = true;

    (async () => {
      const result = await verifyEmail(token);
      if (result.success) {
        setState('done');
        redirectRef.current = setTimeout(() => navigate('/chat'), 1800);
      } else {
        setState('failed');
        setExpired(result.code === 'TOKEN_EXPIRED');
        setMessage(result.message);
      }
    })();
  }, [token, verifyEmail, navigate]);

  useEffect(() => () => clearTimeout(redirectRef.current), []);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setResending(true);
    setResendNote('');
    try {
      await authAPI.resendVerification(email.trim());
      setResendNote('If that address needs confirming, a new link is on its way.');
    } catch {
      setResendNote('Could not send right now. Please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-black px-4">
      <div className="max-w-md w-full bg-gray-800/50 backdrop-blur-md rounded-2xl p-8 border border-gray-700/30 shadow-2xl text-center">
        {children}
      </div>
    </div>
  );

  if (state === 'checking') {
    return shell(<p className="text-gray-400">Confirming your email…</p>);
  }

  if (state === 'done') {
    return shell(
      <>
        <div className="text-4xl mb-3">✅</div>
        <h1 className="text-2xl font-bold text-white mb-2">Email confirmed</h1>
        <p className="text-gray-400">Your free trial has started. Taking you to chat…</p>
      </>
    );
  }

  // 'missing' (someone opened /verify-email by hand) and 'failed' share the
  // same recovery path: ask for the address and send a fresh link.
  return shell(
    <>
      <div className="text-4xl mb-3">{expired ? '⏰' : '⚠️'}</div>
      <h1 className="text-2xl font-bold text-white mb-2">
        {expired ? 'Link expired' : "Couldn't confirm"}
      </h1>
      <p className="text-gray-400 mb-6">
        {state === 'missing'
          ? 'This page needs the link from your confirmation email.'
          : message}
      </p>

      <form onSubmit={handleResend} className="space-y-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Your email address"
          className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
        />
        <button
          type="submit"
          disabled={resending || !email.trim()}
          className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {resending ? 'Sending…' : 'Send me a new link'}
        </button>
      </form>

      {resendNote && <p className="text-teal-300 text-sm mt-4">{resendNote}</p>}

      <Link to="/signin" className="block mt-6 text-blue-400 hover:text-blue-300 text-sm">
        Back to sign in
      </Link>
    </>
  );
};

export default VerifyEmail;
